/**
 * Storage utility for Coding Opportunities overrides and patient status
 * HIPAA Compliance: Uses encrypted storage for PHI
 * Regulation: 45 CFR 164.312(a)(2)(iv) - Encryption and Decryption
 *
 * Structure in storage:
 * {
 *   "[cipId]": {
 *     overrides: {
 *       "[opportunityId]": {
 *         decision: "Current Condition",
 *         comment: "Doctor's note here",
 *         originalDecision: "Approve",
 *         overriddenAt: "2026-01-21T...",
 *         overriddenBy: "username"
 *       }
 *     },
 *     patientStatus: {
 *       status: "Complete",
 *       comment: "Optional comment",
 *       completedAt: "2026-01-21T...",
 *       completedBy: "username"
 *     }
 *   }
 * }
 */

import secureStorage from '../../../utils/security/secureStorage';

const STORAGE_KEY = "coding_op_overrides";

/**
 * Get all data from secure storage
 */
function getAllData() {
  try {
    const data = secureStorage.getItem(STORAGE_KEY);
    return data || {};
  } catch (err) {
    // HIPAA: Don't log error details
    console.error("Error reading coding op overrides from storage");
    return {};
  }
}

/**
 * Save all data to secure storage
 */
function saveAllData(data) {
  try {
    secureStorage.setItem(STORAGE_KEY, data);
  } catch (err) {
    console.error("Error saving coding op overrides to storage");
  }
}

/**
 * Get patient data for a specific cipId
 */
export function getPatientData(cipId) {
  if (!cipId) return { overrides: {}, patientStatus: null };
  const allData = getAllData();
  return allData[cipId] || { overrides: {}, patientStatus: null };
}

/**
 * Get all overrides for a patient
 */
export function getOverrides(cipId) {
  const patientData = getPatientData(cipId);
  return patientData.overrides || {};
}

/**
 * Save an override for a specific coding opportunity
 */
export function saveOverride(cipId, opportunityId, overrideData) {
  if (!cipId || !opportunityId) return;

  const allData = getAllData();
  if (!allData[cipId]) {
    allData[cipId] = { overrides: {}, patientStatus: null };
  }
  if (!allData[cipId].overrides) {
    allData[cipId].overrides = {};
  }

  allData[cipId].overrides[opportunityId] = {
    ...overrideData,
    overriddenAt: new Date().toISOString(),
  };

  saveAllData(allData);
}

/**
 * Remove an override for a specific coding opportunity
 */
export function removeOverride(cipId, opportunityId) {
  if (!cipId || !opportunityId) return;

  const allData = getAllData();
  if (allData[cipId]?.overrides?.[opportunityId]) {
    delete allData[cipId].overrides[opportunityId];
    saveAllData(allData);
  }
}

/**
 * Get patient status for a specific cipId
 */
export function getPatientStatus(cipId) {
  const patientData = getPatientData(cipId);
  return patientData.patientStatus || null;
}

/**
 * Save patient status
 */
export function savePatientStatus(cipId, status, comment, username) {
  if (!cipId || !status) return;

  const allData = getAllData();
  if (!allData[cipId]) {
    allData[cipId] = { overrides: {}, patientStatus: null };
  }

  allData[cipId].patientStatus = {
    status,
    comment: comment || "",
    completedAt: new Date().toISOString(),
    completedBy: username || "unknown",
  };

  saveAllData(allData);
}

/**
 * Clear patient status
 */
export function clearPatientStatus(cipId) {
  if (!cipId) return;

  const allData = getAllData();
  if (allData[cipId]) {
    allData[cipId].patientStatus = null;
    saveAllData(allData);
  }
}

/**
 * Clear all data for a patient
 */
export function clearPatientData(cipId) {
  if (!cipId) return;

  const allData = getAllData();
  delete allData[cipId];
  saveAllData(allData);
}

/**
 * Get all patient statuses from storage
 * Returns an object with cipId as key and patientStatus as value
 */
export function getAllPatientStatuses() {
  const allData = getAllData();
  const statuses = {};

  Object.keys(allData).forEach((cipId) => {
    if (allData[cipId]?.patientStatus) {
      statuses[cipId] = allData[cipId].patientStatus;
    }
  });

  return statuses;
}

/**
 * Get all overrides for all patients
 * Returns an object with cipId as key and overrides object as value
 */
export function getAllOverrides() {
  const allData = getAllData();
  const allOverrides = {};

  Object.keys(allData).forEach((cipId) => {
    if (allData[cipId]?.overrides && Object.keys(allData[cipId].overrides).length > 0) {
      allOverrides[cipId] = allData[cipId].overrides;
    }
  });

  return allOverrides;
}

/**
 * Decision options for both AI and doctor overrides
 * Each option has an id that maps to the backend status code
 */
export const DECISION_OPTIONS = [
  // AI/Doctor decisions with backend IDs
  { id: 0, value: "Pending", label: "Pending", color: "default" },
  { id: 51, value: "Current Condition", label: "Current Condition", color: "info" },
  { id: 52, value: "Non-Existent", label: "Non-Existent", color: "default" },
  { id: 53, value: "Acute Episode", label: "Acute Episode", color: "info" },
  { id: 54, value: "Off Label Use", label: "Off Label Use", color: "warning" },
  { id: 55, value: "Insufficient Details", label: "Insufficient Details", color: "warning" },
  { id: 56, value: "Null", label: "Null", color: "default" },
  { id: 57, value: "Rejected", label: "Rejected", color: "error" },
  { id: 58, value: "Approve", label: "Approve", color: "success" },
  { id: 59, value: "Needs Review", label: "Needs Review", color: "warning" },
];

/**
 * Get decision status ID by value
 * @param {string} value - The decision value (e.g., "Approve")
 * @returns {number|null} - The status ID or null
 */
export function getDecisionStatusId(value) {
  // Normalize AI responses to match our status values
  let normalizedValue = value;

  // AI returns "Reject" but our status is "Rejected"
  if (normalizedValue === "Reject") {
    normalizedValue = "Rejected";
  }

  const option = DECISION_OPTIONS.find((opt) => opt.value === normalizedValue);
  return option?.id ?? null;
}

/**
 * Patient status options for finishing the process
 * Each option has an id that maps to the backend status code
 */
export const PATIENT_STATUS_OPTIONS = [
  { id: 0, value: "Pending", label: "Pending" },
  { id: 1, value: "Open", label: "Open" },
  { id: 2, value: "Charge Slip Completed", label: "Charge Slip Completed" },
  { id: 3, value: "Complete", label: "Complete" },
  { id: 4, value: "Not addressed", label: "Not addressed" },
  { id: 5, value: "Notes not signed by Doctor", label: "Notes not signed by Doctor" },
  { id: 6, value: "Charge Slip not Completed", label: "Charge Slip not Completed" },
  { id: 7, value: "Notes Signed by Doctor", label: "Notes Signed by Doctor" },
];

/**
 * Get color for a decision value
 */
export function getDecisionColor(decision) {
  // Normalize AI responses
  let normalizedDecision = decision;
  if (normalizedDecision === "Reject") {
    normalizedDecision = "Rejected";
  }

  const option = DECISION_OPTIONS.find((opt) => opt.value === normalizedDecision);
  return option?.color || "default";
}
